import type { EstimatedTimeName } from '@gat-ship/types'

/**
 * Functional core — selectors over `EstimatedTimeName[]`.
 *
 * Every function is pure: it takes the current list (read-only) and returns a
 * fresh value, never mutating the input and never touching a ref, the network,
 * or loading state. The imperative shell (`useEstimatedTimeNamesDataSource`)
 * owns the single write point that assigns these results to the cache.
 */

/** Select the active (non-inactive) entries. Read-only. */
export function selectActiveEstimatedTimeNames(
  items: readonly EstimatedTimeName[]
): EstimatedTimeName[] {
  return items.filter((e) => !e.isInactive)
}

/**
 * Resolve an entry by matching `HH:mm:ss` to `referenceTime`. Returns the
 * first match, or `undefined` when none. Read-only.
 */
export function findEstimatedTimeNameByReferenceTime(
  items: readonly EstimatedTimeName[],
  referenceTime: string
): EstimatedTimeName | undefined {
  return items.find((e) => e.referenceTime === referenceTime)
}

/**
 * Sort entries by `referenceTime` (`HH:mm:ss`, ascending) using
 * `localeCompare`. Returns a new array; never mutates the input.
 */
export function sortEstimatedTimeNamesByReferenceTime(
  items: readonly EstimatedTimeName[]
): EstimatedTimeName[] {
  return [...items].sort((a, b) =>
    a.referenceTime.localeCompare(b.referenceTime)
  )
}
